import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabaseClient";
import { formatAdminDate, getSupabaseErrorMessage } from "./adminConfig";

function formatLastActive(value) {
  if (!value) return "Not recorded yet";
  return formatAdminDate(value);
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [itemCounts, setItemCounts] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentAuthUserId, setCurrentAuthUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [levels, setLevels] = useState([]);
  const [currentAdminLevel, setCurrentAdminLevel] = useState(null);
  const [levelSelection, setLevelSelection] = useState("");
  const [savingLevel, setSavingLevel] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");

    const [{ data, error }, { data: authData }, { data: levelRows, error: levelsError }] = await Promise.all([
      supabase.from("users").select("*").order("created_at", { ascending: false }).limit(250),
      supabase.auth.getUser(),
      supabase.from("user_levels").select("id, name, level").order("level", { ascending: false }),
    ]);

    if (levelsError) console.warn("Unable to load user levels:", levelsError);
    setLevels(levelRows || []);

    setCurrentAuthUserId(authData?.user?.id || null);

    if (error) {
      console.error("Unable to load admin users:", error);
      setErrorMessage(getSupabaseErrorMessage(error, "Unable to load users."));
      setLoading(false);
      return;
    }

    const rows = data || [];
    setUsers(rows);

    const authUserId = authData?.user?.id || null;
    const callerRow = rows.find((row) => row.id === authUserId);
    const callerLevel = (levelRows || []).find((level) => Number(level.id) === Number(callerRow?.user_level_id));
    setCurrentAdminLevel(callerLevel || null);

    const ids = rows.map((user) => user.id).filter(Boolean);
    const counts = {};

    if (ids.length) {
      const { data: userItems, error: itemError } = await supabase
        .from("user_items")
        .select("user_id")
        .in("user_id", ids);

      if (itemError) {
        console.warn("Unable to calculate user item counts:", itemError);
      } else {
        (userItems || []).forEach((row) => {
          counts[row.user_id] = (counts[row.user_id] || 0) + 1;
        });
      }
    }

    setItemCounts(counts);
    setSelectedUser((current) => rows.find((row) => row.id === current?.id) || null);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    setDeleteOpen(false);
    setDeleteConfirm("");
    setLevelSelection(selectedUser?.user_level_id ? String(selectedUser.user_level_id) : "");
  }, [selectedUser?.id, selectedUser?.user_level_id]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users;

    return users.filter((user) => {
      const haystack = [
        user.email,
        user.username,
        user.display_name,
        user.first_name,
        user.last_name,
        user.id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [users, search]);

  const deleteConfirmationText = selectedUser?.email || selectedUser?.username || selectedUser?.id || "";
  const deletingSelf = Boolean(selectedUser?.id && selectedUser.id === currentAuthUserId);
  const deleteConfirmed = Boolean(deleteConfirmationText && deleteConfirm.trim() === deleteConfirmationText);


  const selectedLevel = levels.find((level) => Number(level.id) === Number(selectedUser?.user_level_id));
  const callerLevelValue = Number(currentAdminLevel?.level || 0);
  const selectedLevelValue = Number(selectedLevel?.level || 0);
  const editingSelf = Boolean(selectedUser?.id && selectedUser.id === currentAuthUserId);
  const canManageSelected = Boolean(selectedUser && !editingSelf && callerLevelValue >= 50 && selectedLevelValue <= callerLevelValue);
  const assignableLevels = levels.filter((level) => Number(level.level) <= callerLevelValue);
  const levelChanged = Boolean(levelSelection && Number(levelSelection) !== Number(selectedUser?.user_level_id));

  const handleUpdateLevel = async () => {
    if (!selectedUser?.id || !canManageSelected || !levelChanged || savingLevel) return;

    setSavingLevel(true);
    setErrorMessage("");
    try {
      const { data, error } = await supabase.functions.invoke("admin-update-user-level", {
        body: { user_id: selectedUser.id, user_level_id: Number(levelSelection) },
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || "Unable to update user level.");

      const updatedLevelId = Number(data.user_level_id);
      setUsers((current) => current.map((user) =>
        user.id === selectedUser.id ? { ...user, user_level_id: updatedLevelId } : user
      ));
      setSelectedUser((current) => current ? { ...current, user_level_id: updatedLevelId } : current);
    } catch (error) {
      console.error("Unable to update user level:", error);
      setErrorMessage(getSupabaseErrorMessage(error, "Unable to update this user's level."));
    } finally {
      setSavingLevel(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser?.id || deletingSelf || !deleteConfirmed || deleting) return;

    setDeleting(true);
    setErrorMessage("");

    try {
      const { data, error } = await supabase.functions.invoke("admin-delete-user", {
        body: { user_id: selectedUser.id },
      });

      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || "User deletion failed.");

      const deletedId = selectedUser.id;
      setUsers((current) => current.filter((user) => user.id !== deletedId));
      setItemCounts((current) => {
        const next = { ...current };
        delete next[deletedId];
        return next;
      });
      setSelectedUser(null);
      setDeleteOpen(false);
      setDeleteConfirm("");
    } catch (error) {
      console.error("Unable to delete user:", error);
      setErrorMessage(getSupabaseErrorMessage(error, "Unable to delete this user."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="hadmin-toolbar">
        <input
          className="hadmin-input hadmin-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search email, username, or user ID..."
        />
        <button type="button" className="hadmin-btn-secondary" onClick={fetchUsers}>
          Refresh
        </button>
      </div>

      {errorMessage && <div className="hadmin-error">{errorMessage}</div>}

      <div className="hadmin-split">
        <section className="hadmin-panel">
          <div className="hadmin-row-between" style={{ marginBottom: 14 }}>
            <strong>User Directory</strong>
            <span className="hadmin-pill">Showing {filteredUsers.length} / {users.length}</span>
          </div>

          {loading ? (
            <div className="hadmin-empty">Loading users...</div>
          ) : (
            <div className="hadmin-table-wrap">
              <table className="hadmin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Joined</th>
                    <th>Last Active</th>
                    <th>Items</th>
                    <th>Level ID</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr
                      key={user.id}
                      className={selectedUser?.id === user.id ? "selected" : ""}
                      onClick={() => setSelectedUser(user)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>
                        <strong>{user.username || user.display_name || user.email || "User"}</strong>
                        <div className="hadmin-muted">{user.email || user.id}</div>
                      </td>
                      <td>{formatAdminDate(user.created_at)}</td>
                      <td>{formatLastActive(user.last_active_at)}</td>
                      <td>{itemCounts[user.id] || 0}</td>
                      <td>{user.user_level_id || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="hadmin-panel">
          {selectedUser ? (
            <>
              <div className="hadmin-section-heading">
                <div>
                  <h2>{selectedUser.username || selectedUser.display_name || selectedUser.email || "User"}</h2>
                  <p>{selectedUser.email || selectedUser.id}</p>
                </div>
                <span className="hadmin-pill blue">{itemCounts[selectedUser.id] || 0} items</span>
              </div>

              <div className="hadmin-detail-grid">
                <div className="hadmin-detail-box">
                  <span>User ID</span>
                  <div style={{ wordBreak: "break-all" }}>{selectedUser.id}</div>
                </div>
                <div className="hadmin-detail-box">
                  <span>Joined</span>
                  <div>{formatAdminDate(selectedUser.created_at)}</div>
                </div>
                <div className="hadmin-detail-box">
                  <span>Last Active</span>
                  <div>{formatLastActive(selectedUser.last_active_at)}</div>
                </div>
                <div className="hadmin-detail-box">
                  <span>Username</span>
                  <div>{selectedUser.username || "—"}</div>
                </div>
                <div className="hadmin-detail-box">
                  <span>User Level</span>
                  <div>{selectedLevel ? `${selectedLevel.name} (${selectedLevel.level})` : selectedUser.user_level_id || "—"}</div>
                </div>
              </div>

              <div style={{ marginTop: 18, paddingTop: 18, borderTop: "1px solid rgba(148, 163, 184, 0.2)" }}>
                <h3 style={{ margin: "0 0 8px" }}>User Level</h3>
                {editingSelf ? (
                  <p className="hadmin-muted" style={{ margin: 0 }}>Your own level cannot be changed from this screen.</p>
                ) : selectedLevelValue > callerLevelValue ? (
                  <p className="hadmin-muted" style={{ margin: 0 }}>This user has a higher permission level than your account.</p>
                ) : (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <select
                      className="hadmin-input"
                      value={levelSelection}
                      onChange={(event) => setLevelSelection(event.target.value)}
                      disabled={!canManageSelected || savingLevel}
                    >
                      {assignableLevels.map((level) => (
                        <option key={level.id} value={level.id}>{level.name} ({level.level})</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="hadmin-btn-secondary"
                      disabled={!canManageSelected || !levelChanged || savingLevel}
                      onClick={handleUpdateLevel}
                    >
                      {savingLevel ? "Saving..." : "Save Level"}
                    </button>
                  </div>
                )}
              </div>

              <h3 style={{ marginTop: 18 }}>Raw User Record</h3>
              <pre className="hadmin-json">{JSON.stringify(selectedUser, null, 2)}</pre>

              <div style={{ marginTop: 20, paddingTop: 18, borderTop: "1px solid rgba(239, 68, 68, 0.28)" }}>
                <h3 style={{ margin: "0 0 6px", color: "#fca5a5" }}>Delete User Account</h3>
                {deletingSelf ? (
                  <p className="hadmin-muted" style={{ margin: 0 }}>
                    You cannot delete the account you are currently signed into from this screen.
                  </p>
                ) : !deleteOpen ? (
                  <>
                    <p className="hadmin-muted" style={{ margin: "0 0 12px" }}>
                      Permanently removes this user's Supabase Auth account. Related Hobbli data follows the database's existing foreign-key deletion rules.
                    </p>
                    <button type="button" className="hadmin-btn-danger" onClick={() => setDeleteOpen(true)}>
                      Delete User Account
                    </button>
                  </>
                ) : (
                  <div>
                    <p className="hadmin-muted" style={{ margin: "0 0 10px" }}>
                      Type <strong style={{ color: "#e2e8f0" }}>{deleteConfirmationText}</strong> to confirm permanent deletion.
                    </p>
                    <input
                      className="hadmin-input"
                      value={deleteConfirm}
                      onChange={(event) => setDeleteConfirm(event.target.value)}
                      placeholder={deleteConfirmationText}
                      autoComplete="off"
                      style={{ width: "100%", marginBottom: 10 }}
                    />
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        className="hadmin-btn-danger"
                        disabled={!deleteConfirmed || deleting}
                        onClick={handleDeleteUser}
                      >
                        {deleting ? "Deleting..." : "Permanently Delete User"}
                      </button>
                      <button
                        type="button"
                        className="hadmin-btn-secondary"
                        disabled={deleting}
                        onClick={() => {
                          setDeleteOpen(false);
                          setDeleteConfirm("");
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="hadmin-empty">Select a user to inspect the account record.</div>
          )}
        </section>
      </div>
    </div>
  );
}
